import { MouseEvent, useMemo, useState } from 'react'
import { SkuBackfillItem, SkuBackfillRequest, SkuBackfillResult } from '@/console/api/types'
import { SkuBackfillController, useSkuBackfill } from '@/console/data/devConsoleSkus'
import { SkuBackfillRun } from '@/console/state/skuBackfillRunStorage'
import { useReportBusy } from '@/console/state/useReportBusy'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { DataTable, DataTableRow } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { Tag } from '@/console/ui/Tag'
import {
  ParsedBackfillInput,
  SKU_BACKFILL_MAX_ITEMS,
  SKU_BACKFILL_OUTCOMES,
  SKU_BACKFILL_OUTCOME_TONE,
  SKU_BACKFILL_RETRY,
  SKU_BACKFILL_RETRY_BY_OUTCOME,
  SKU_BACKFILL_RETRY_LABEL,
  SKU_BACKFILL_TONE_TAG,
  SKU_BACKFILL_VALUE_DESCRIPTION,
  SKU_ID_MAX_LENGTH,
  exceedsBackfillLimit,
  findKnownCurrentSkuId,
  isSubmittableBackfill,
  parseBackfillInput,
  toBackfillInputText,
  toRetryItem,
} from './skuBackfill'
import { formatOutcomeSummary } from './skuLookup'

const SECTION_ID = 's-fopt-sku-fill'
const REDISPATCH_SECTION_ID = 's-fopt-redispatch'
const HINT_STYLE = { fontSize: 12.5, color: 'var(--ink-3)' } as const

// 주소창 해시가 곧 탭 상태라 앵커 기본 동작으로 이동하면 탭이 풀린다 — 스크롤만 한다.
const scrollToSection = (id: string): void => {
  document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
}

const formatSubmittedAt = (submittedAt: string): string => new Date(submittedAt).toLocaleString('ko-KR')

type BackfillForm = {
  raw: string
  setRaw: (raw: string) => void
  overwrite: boolean
  setOverwrite: (overwrite: boolean) => void
  parsed: ParsedBackfillInput
}

const useBackfillForm = (): BackfillForm => {
  const [raw, setRaw] = useState('')
  const [overwrite, setOverwrite] = useState(false)
  const parsed = useMemo(() => parseBackfillInput(raw), [raw])
  return { raw, setRaw, overwrite, setOverwrite, parsed }
}

const BackfillInputFeedback = ({ parsed }: { parsed: ParsedBackfillInput }): JSX.Element => (
  <>
    <div style={HINT_STYLE}>
      유효 {parsed.items.length}건 · 중복 제외 {parsed.duplicates}건 · 최대 {SKU_BACKFILL_MAX_ITEMS}건
    </div>
    {exceedsBackfillLimit(parsed) ? <Callout tone="crit">한 번에 최대 {SKU_BACKFILL_MAX_ITEMS}건까지 백필할 수 있어요.</Callout> : null}
    {parsed.invalidLines.length > 0 ? (
      <Callout tone="crit">
        형식이 맞지 않는 줄이 있어요(id 는 양수, skuId 는 1~{SKU_ID_MAX_LENGTH}자): {parsed.invalidLines.join(' / ')}
      </Callout>
    ) : null}
    {parsed.conflictingIds.length > 0 ? (
      <Callout tone="crit">같은 standardOrderProductId 에 서로 다른 skuId 가 있어요: {parsed.conflictingIds.join(', ')}</Callout>
    ) : null}
    {parsed.orderNumberLikeIds.length > 0 ? (
      <Callout tone="warn">주문번호처럼 보이는 값이 있어요: {parsed.orderNumberLikeIds.join(', ')} — standard_order_product.id 가 맞는지 확인하세요.</Callout>
    ) : null}
  </>
)

type BackfillFormFieldsProps = { form: BackfillForm; submitting: boolean; submitDisabled: boolean; overwriteBlocked: boolean; onSubmit: () => void }

const BackfillFormFields = ({ form, submitting, submitDisabled, overwriteBlocked, onSubmit }: BackfillFormFieldsProps): JSX.Element => (
  <Stack>
    <Field label="standardOrderProductId skuId — 한 줄에 한 건. standard_order_product.id 이며 standardOrderId(주문 id)가 아니에요">
      <textarea
        rows={4}
        spellCheck={false}
        placeholder={'9403674 S2509090000029\n(standard_order_product.id 이지 standardOrderId 가 아니에요)'}
        value={form.raw}
        disabled={submitting}
        onChange={(event) => form.setRaw(event.target.value)}
      />
    </Field>
    <BackfillInputFeedback parsed={form.parsed} />
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, cursor: 'pointer' }}>
      <input type="checkbox" checked={form.overwrite} disabled={submitting} onChange={(event) => form.setOverwrite(event.target.checked)} />
      <span>overwrite — 값이 이미 있는 행도 바꿔요 (기본 꺼짐)</span>
    </label>
    {overwriteBlocked ? <Callout tone="warn">응답을 못 받은 제출이 있어 overwrite 제출을 막았어요. overwrite 를 끄고 다시 보내 현재 값부터 확인하세요.</Callout> : null}
    <div>
      <Button disabled={submitDisabled} onClick={onSubmit}>
        {submitting ? '백필 중…' : '백필'}
      </Button>
    </div>
  </Stack>
)

/** null 이면 제출 중이라 재시도 버튼을 잠근다 — 확인창을 열어도 제출이 무시된다. */
type RetryHandler = ((result: SkuBackfillResult, run: SkuBackfillRun) => void) | null

const RetryButton = ({ result, run, onRetry }: { result: SkuBackfillResult; run: SkuBackfillRun; onRetry: RetryHandler }): JSX.Element | null => {
  const retry = SKU_BACKFILL_RETRY_BY_OUTCOME[result.outcome]
  if (retry === null) return null
  return (
    <Button variant="line" size="sm" disabled={onRetry === null} onClick={() => onRetry?.(result, run)}>
      {SKU_BACKFILL_RETRY_LABEL[retry]}
    </Button>
  )
}

const RUN_COLUMNS = [
  { key: 'id', label: 'standardOrderProductId', mono: true },
  { key: 'outcome', label: '판정' },
  { key: 'value', label: 'skuId', mono: true },
  { key: 'retry', label: '다음 조치' },
]

const toRunRows = (run: SkuBackfillRun, onRetry: RetryHandler): DataTableRow[] =>
  run.results.map((result, index) => ({
    key: `${index}:${result.standardOrderProductId}`,
    cells: {
      id: result.standardOrderProductId,
      outcome: <Tag tone={SKU_BACKFILL_TONE_TAG[SKU_BACKFILL_OUTCOME_TONE[result.outcome]]}>{result.outcome}</Tag>,
      value: SKU_BACKFILL_VALUE_DESCRIPTION[result.outcome](result),
      retry: <RetryButton result={result} run={run} onRetry={onRetry} />,
    },
  }))

const BackfillRunTable = ({ run, onRetry }: { run: SkuBackfillRun; onRetry: RetryHandler }): JSX.Element => (
  <Stack gap={6}>
    <div style={HINT_STYLE}>
      {formatSubmittedAt(run.submittedAt)} 제출 · overwrite={String(run.overwrite)}
    </div>
    <div style={{ fontSize: 13.5, fontWeight: 700 }}>
      {formatOutcomeSummary(
        run.results.map((result) => result.outcome),
        SKU_BACKFILL_OUTCOMES,
      )}
    </div>
    <DataTable maxHeight={480} stickyHeader columns={RUN_COLUMNS} rows={toRunRows(run, onRetry)} />
  </Stack>
)

const RedispatchGuide = (): JSX.Element => {
  const jump = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault()
    scrollToSection(REDISPATCH_SECTION_ID)
  }
  return (
    <Note>
      백필은 sku_id 한 컬럼만 바꿔요. 실패한 F-OPT 는 재발사가 필요할 수 있습니다 —{' '}
      <a href={`#${REDISPATCH_SECTION_ID}`} onClick={jump}>
        재발사 섹션으로 이동
      </a>
      . 분할 주문은 자식 주문의 상품 행 id 도 각각 넣어야 해요.
    </Note>
  )
}

const BackfillResults = ({ backfill, onRetry }: { backfill: SkuBackfillController; onRetry: RetryHandler }): JSX.Element => (
  <Stack>
    <SectionHeading level={3} title="백필 결과" count={backfill.runs.length > 0 ? `최근 ${backfill.runs.length}회` : undefined} />
    {backfill.submitting ? <Callout>백필하는 중…</Callout> : null}
    {backfill.error ? <Callout tone="crit">{backfill.error.message}</Callout> : null}
    {backfill.runs.length === 0 ? <Callout>아직 백필 결과가 없어요.</Callout> : null}
    {backfill.runs.map((run) => (
      <BackfillRunTable key={run.id} run={run} onRetry={backfill.submitting ? null : onRetry} />
    ))}
    <RedispatchGuide />
  </Stack>
)

type ConfirmProps = { pending: SkuBackfillRequest | null; runs: SkuBackfillRun[]; onConfirm: () => void; onCancel: () => void }

// 현재 값은 지난 응답에서 온 것이라 그 뒤에 바뀌었을 수 있다 — 어느 제출 기준인지 함께 보인다.
const describeKnownCurrentSkuId = (runs: SkuBackfillRun[], standardOrderProductId: number): string => {
  const known = findKnownCurrentSkuId(runs, standardOrderProductId)
  if (!known) return '현재 값 모름'
  return `${known.skuId} (${formatSubmittedAt(known.submittedAt)} 제출 기준)`
}

const describePendingItem = (item: SkuBackfillItem, overwrite: boolean, runs: SkuBackfillRun[]): string => {
  if (!overwrite) return `${item.standardOrderProductId}: ${item.skuId}`
  return `${item.standardOrderProductId}: ${describeKnownCurrentSkuId(runs, item.standardOrderProductId)} → ${item.skuId}`
}

const BackfillConfirmBody = ({ pending, runs }: { pending: SkuBackfillRequest; runs: SkuBackfillRun[] }): JSX.Element => (
  <>
    <p style={{ margin: 0 }}>
      {pending.items.length}건 · overwrite={String(pending.overwrite)}
    </p>
    {pending.overwrite ? (
      <Callout tone="crit">기존 값을 덮어씁니다. 잘못 덮어쓰면 되돌릴 근거는 결과의 previousSkuId 뿐이에요.</Callout>
    ) : (
      <p style={{ margin: '10px 0 0' }}>비어 있는(NULL) 행만 채워요. 값이 있는 행은 SKIPPED_ALREADY_FILLED 로 현재 값을 돌려받아요.</p>
    )}
    <ul style={{ margin: '10px 0 0', paddingLeft: 18, maxHeight: 200, overflow: 'auto', fontFamily: 'var(--mono)', fontSize: 12.5 }}>
      {pending.items.map((item) => (
        <li key={item.standardOrderProductId}>{describePendingItem(item, pending.overwrite, runs)}</li>
      ))}
    </ul>
  </>
)

const BackfillConfirmDialog = ({ pending, runs, onConfirm, onCancel }: ConfirmProps): JSX.Element => (
  <ConfirmDialog
    open={pending !== null}
    title="sku_id 를 백필할까요?"
    body={pending ? <BackfillConfirmBody pending={pending} runs={runs} /> : null}
    confirmLabel="백필"
    danger={pending?.overwrite ?? false}
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
)

// 현재 값을 모르니 overwrite 없이 다시 보내 현재 값을 돌려받게 입력란에만 담는다.
const fillInputForRecheck = (form: BackfillForm, result: SkuBackfillResult): void => {
  form.setRaw(toBackfillInputText([toRetryItem(result)]))
  form.setOverwrite(false)
  scrollToSection(SECTION_ID)
}

/**
 * sku_id 백필(`s-fopt-sku-fill`). 대상 id 를 내려주는 조회가 없어 사람이 붙여넣는다.
 * 제출 중에는 `onBusyChange(true)` 로 셸의 탭 전환을 잠근다 — 응답을 잃으면 previousSkuId 를 잃는다.
 */
export const SkuBackfillPanel = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const backfill = useSkuBackfill()
  const form = useBackfillForm()
  const [pending, setPending] = useState<SkuBackfillRequest | null>(null)

  useReportBusy(backfill.submitting, onBusyChange)

  const overwriteBlocked = backfill.overwriteLocked && form.overwrite
  // 확인창이 떠 있는 동안 막지 않으면 키보드로 다시 눌러 확인창의 대상이 몰래 바뀐다.
  const submitDisabled = !isSubmittableBackfill(form.parsed) || backfill.submitting || pending !== null || overwriteBlocked

  const requestConfirm = (): void => setPending({ items: form.parsed.items, overwrite: form.overwrite })

  const confirm = (): void => {
    if (pending) backfill.submit(pending)
    setPending(null)
  }

  const retry = (result: SkuBackfillResult, run: SkuBackfillRun): void => {
    // 응답을 못 받은 제출 뒤에는 overwrite 로 다시 보내지 않고 현재 값부터 확인하게 한다.
    const resendsAsIs = SKU_BACKFILL_RETRY_BY_OUTCOME[result.outcome] === SKU_BACKFILL_RETRY.resend && !(run.overwrite && backfill.overwriteLocked)
    if (resendsAsIs) {
      setPending({ items: [toRetryItem(result)], overwrite: run.overwrite })
      return
    }
    fillInputForRecheck(form, result)
  }

  return (
    <section>
      <SectionHeading id={SECTION_ID} title="sku_id 백필" badge={<SourceBadge tone="new">신규 · POST /dev-console/order-products/sku-backfill</SourceBadge>} />
      <BackfillFormFields
        form={form}
        submitting={backfill.submitting}
        submitDisabled={submitDisabled}
        overwriteBlocked={overwriteBlocked}
        onSubmit={requestConfirm}
      />
      <BackfillResults backfill={backfill} onRetry={retry} />
      <BackfillConfirmDialog pending={pending} runs={backfill.runs} onConfirm={confirm} onCancel={() => setPending(null)} />
    </section>
  )
}
